import { Button, useDataProvider } from "react-admin";
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  makeStyles,
  TextField,
  Typography,
} from "@material-ui/core";
import React, { useCallback, useEffect, useState } from "react";
import { useField } from "react-final-form";
import { debounce, find } from "lodash";
import SanitizedGrid from "../../../components/SanitizedGrid";
import { SchoolIdSelected } from "../../../utils/tools";
import { getFixedLocationDropOffBySchoolIds } from "../tools";
import Autocomplete from "@material-ui/lab/Autocomplete";
import Alert from "@material-ui/lab/Alert";
import { AlertTitle } from "@material-ui/lab";

const useStyles = makeStyles({
  dialog: {
    "& .MuiDialog-paper": {
      overflowY: "unset",
      width: "100%",
    },
  },
  studentSearchInput: {
    marginTop: "8px",
    paddingBottom: "3px",
    "& .MuiOutlinedInput-root": {
      "& fieldset": {
        borderColor: "#EAEAEA",
      },
      "&:hover fieldset": {
        borderColor: "#EAEAEA",
      },
    },
  },
});

const AddStudentButton = () => {
  const [students, setStudents] = useState<IStudent[]>([]);
  const [studentAddresses, setStudentAddresses] = useState<IStudentAddress[]>(
    [],
  );
  const [selectedStudent, setSelectedStudent] = useState<any>(null);
  const [selectedStudentAddress, setSelectedStudentAddress] =
    useState<IStudentAddress>(null);

  const [errorMessage, setErrorMessage] = useState<string>("");
  const [isDisable, setIsDisable] = useState<boolean>(true);
  const [searchValue, setSearchValue] = useState<string>("");
  const [open, setOpen] = useState<boolean>(false);

  const busScheduleField = useField("busSchedule");
  const studentsField = useField("students");
  const formStudentsChangedField = useField("formStudentsChanged");
  const routesField = useField("routes");

  const newStudentAddress = useField("newStudentAddress");
  const locationData = useField("locationData");

  const classes = useStyles();
  const provider = useDataProvider();
  const schoolIdSelected = SchoolIdSelected();

  const handleOpenDialog = () => {
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
    setErrorMessage("");
    newStudentAddress.input.onChange(null);
    locationData.input.onChange(null);
  };

  const handleSaveAddStudent = () => {
    selectedStudent.address = selectedStudentAddress;
    studentsField.input.onChange([
      selectedStudent,
      ...studentsField.input.value,
    ]);
    formStudentsChangedField.input.onChange(true);
    handleClose();
  };

  const getStudents = useCallback(
    debounce((searchString: string) => {
      const filter: any = { SchoolId: schoolIdSelected, Status: "active" };

      if (busScheduleField.input.value.type === "cca") {
        filter.HasCcaSchedule = true;
      }

      if (
        busScheduleField.input.value.type === "to_school" ||
        busScheduleField.input.value.type === "from_school"
      ) {
        filter.HasRegularSchedule = true;
      }

      provider
        .customGetListWithStaticParams(
          `students?filter=${JSON.stringify(
            filter,
          )}&pageSize=50&search=${searchString}`,
        )
        .then((result: any) => {
          setStudents(result.data as IStudent[]);
        });
    }, 1000),
    [busScheduleField.input.value],
  );

  const getStudentAddress = async () => {
    try {
      let _studentAddress = await provider.getList("studentAddresses", {
        filter: { StudentId: Number(selectedStudent.id) },
        pagination: {
          page: 1,
          perPage: 1000,
        },
        sort: {
          field: "Id",
          order: "ASC",
        },
      });
      let dataFixLocation: any = [];

      if (busScheduleField.input.value.type === "cca") {
        dataFixLocation = await getFixedLocationDropOffBySchoolIds(
          schoolIdSelected,
          provider,
        );
      }

      setStudentAddresses([..._studentAddress.data, ...dataFixLocation]);
    } catch (error) {
      console.error("Failed to fetch student addresses:", error);
      setStudentAddresses([]); // Fallback to empty array
    }
  };

  useEffect(() => {
    if (searchValue !== "") {
      getStudents(searchValue);
    }
  }, [searchValue, getStudents]);

  useEffect(() => {
    if (selectedStudent !== null && selectedStudentAddress !== null) {
      setIsDisable(false);
    } else {
      setIsDisable(true);
    }
  }, [selectedStudent, selectedStudentAddress]);

  useEffect(() => {
    if (selectedStudent !== null) {
      getStudentAddress();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedStudent]);

  return (
    <>
      <Button label="Add Student" onClick={handleOpenDialog} />

      <Dialog
        open={open}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
        className={classes.dialog}
        maxWidth="md"
      >
        <DialogTitle id="alert-dialog-title">Add Student</DialogTitle>
        <DialogContent>
          <SanitizedGrid container spacing={2}>
            {routesField.input.value.length > 0 && (
              <Grid item xs={12}>
                <Alert severity="warning">
                  <AlertTitle>Warning</AlertTitle>

                  <Typography variant="body2">
                    Please be advised that any changes to the student list or
                    address will reset all existing route data.
                  </Typography>
                </Alert>
              </Grid>
            )}

            <Grid item xs={12}>
              <Typography variant="subtitle2">Select Student</Typography>
            </Grid>
            <Grid item xs={12}>
              <Autocomplete
                className={classes.studentSearchInput}
                freeSolo
                disableClearable
                options={students.map((option: IStudent) => {
                  return `${option.id} - ${option.firstName} ${option.lastName}`;
                })}
                onChange={(event, value) => {
                  const splitedValue = value.split(" ");

                  const findExistingStudent = find(studentsField.input.value, [
                    "id",
                    Number(splitedValue[0]),
                  ]);

                  if (findExistingStudent) {
                    setErrorMessage("Student already added");
                    return;
                  }

                  setErrorMessage("");
                  setSelectedStudent(
                    find(students, ["id", Number(splitedValue[0])]),
                  );
                }}
                onInputChange={(event, newInputValue) => {
                  setSearchValue(newInputValue);
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    error={errorMessage !== ""}
                    helperText={errorMessage}
                    label="Search Student"
                    variant="outlined"
                    InputProps={{ ...params.InputProps, type: "search" }}
                  />
                )}
              />
            </Grid>

            <Grid item xs={12}>
              <Typography variant="subtitle2">
                Select Student address
              </Typography>
            </Grid>
            <Grid item xs={12}>
              <Autocomplete
                className={classes.studentSearchInput}
                freeSolo
                disableClearable
                options={studentAddresses.map((option: any) => {
                  let type = "";
                  if (option.isMain === true) {
                    type = "MAIN";
                  } else if (option.isMain === false) {
                    type = "SUB";
                  } else {
                    type = option.name;
                  }
                  return `${option.id} - ${option.blockNumber} ${option.street} ${option.postalCode} - ${type}`;
                })}
                onChange={(event, value) => {
                  const splitedValue = value.split(" ");

                  setSelectedStudentAddress(
                    find(studentAddresses, ["id", Number(splitedValue[0])]),
                  );
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Search Student Address"
                    variant="outlined"
                    InputProps={{ ...params.InputProps, type: "search" }}
                  />
                )}
              />
            </Grid>
          </SanitizedGrid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose} color="secondary" label="Back" />
          <Button
            onClick={handleSaveAddStudent}
            color="primary"
            label="Save"
            disabled={isDisable}
          />
        </DialogActions>
      </Dialog>
    </>
  );
};

export default AddStudentButton;
